/** Route plotting over relays (M3 spec §6.1): fewest hops, ties broken by map distance. */
import type { Galaxy } from './types'

export interface RouteOptions {
  /** Only travel over online relays (long relays start offline, so they drop out too). */
  onlineOnly: boolean
  /** System ids to route around. The start and the destination are never avoided. */
  avoid: ReadonlySet<string>
}

/**
 * Dijkstra on (hops, Euclidean map length), compared lexicographically, so the length only ever breaks ties
 * between routes with the same hop count. Returns the system ids from `fromId` to `toId`, or null if unreachable.
 */
export function planRoute(galaxy: Galaxy, fromId: string, toId: string, opts: RouteOptions): string[] | null {
  const systems = galaxy.systems
  const n = systems.length
  const index = new Map(systems.map((s, i) => [s.id, i]))
  const from = index.get(fromId)
  const to = index.get(toId)
  if (from === undefined || to === undefined) return null
  if (from === to) return [fromId]

  const adj: number[][] = Array.from({ length: n }, () => [])
  for (const r of galaxy.relays) {
    if (opts.onlineOnly && !r.online) continue
    const a = index.get(r.a)
    const b = index.get(r.b)
    if (a === undefined || b === undefined) continue
    adj[a].push(b)
    adj[b].push(a)
  }
  const blocked = (i: number): boolean => i !== from && i !== to && opts.avoid.has(systems[i].id)
  const length = (a: number, b: number): number => {
    const [ax, ay, az] = systems[a].mapPos
    const [bx, by, bz] = systems[b].mapPos
    return Math.hypot(ax - bx, ay - by, az - bz)
  }

  const hops = new Array<number>(n).fill(Infinity)
  const dist = new Array<number>(n).fill(Infinity)
  const prev = new Array<number>(n).fill(-1)
  const done = new Array<boolean>(n).fill(false)
  hops[from] = 0
  dist[from] = 0
  // ponytail: O(n²) Dijkstra with a linear scan instead of a heap; n is about 48
  for (;;) {
    let u = -1
    for (let i = 0; i < n; i++) {
      if (done[i] || hops[i] === Infinity) continue
      if (u === -1 || hops[i] < hops[u] || (hops[i] === hops[u] && dist[i] < dist[u])) u = i
    }
    if (u === -1 || u === to) break
    done[u] = true
    for (const v of adj[u]) {
      if (done[v] || blocked(v)) continue
      const h = hops[u] + 1
      const d = dist[u] + length(u, v)
      if (h < hops[v] || (h === hops[v] && d < dist[v])) {
        hops[v] = h
        dist[v] = d
        prev[v] = u
      }
    }
  }
  if (hops[to] === Infinity) return null
  const path: string[] = []
  for (let i = to; i !== -1; i = prev[i]) path.push(systems[i].id)
  return path.reverse()
}
